/**
 * PILLAR 4 — Theme toggle (Field Black ↔ Daylight).
 *
 * Persists to localStorage and applies via a `data-theme` attribute on the
 * root <html> element so every CSS variable flips in one step. No layout
 * shift; no per-component opt-in required.
 *
 * Use the named exports:
 *   - useTheme() — hook returning { theme, setTheme }
 *   - ThemeToggle — small button suitable for the user menu / header
 */
import { useEffect, useState, useCallback } from "react";
import { Sun, Moon } from "lucide-react";

const STORAGE_KEY = "fop_theme";
const DAYLIGHT = "daylight";
const FIELD_BLACK = "field-black";

function _applyTheme(theme) {
  try {
    const root = document.documentElement;
    if (theme === DAYLIGHT) {
      root.setAttribute("data-theme", DAYLIGHT);
    } else {
      root.removeAttribute("data-theme");
    }
  } catch { /* swallow */ }
}

function _readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === DAYLIGHT ? DAYLIGHT : FIELD_BLACK;
  } catch {
    return FIELD_BLACK;
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState(_readStored);

  useEffect(() => {
    _applyTheme(theme);
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* swallow */ }
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next === DAYLIGHT ? DAYLIGHT : FIELD_BLACK);
  }, []);

  return { theme, setTheme };
}

export function ThemeToggle({ className = "" }) {
  const { theme, setTheme } = useTheme();
  const isDay = theme === DAYLIGHT;
  return (
    <button
      type="button"
      onClick={() => setTheme(isDay ? FIELD_BLACK : DAYLIGHT)}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-[12px] font-mono uppercase tracking-wider border border-[var(--fop-border)] text-[var(--fop-text-2)] hover:text-[var(--fop-text)] hover:bg-[var(--fop-surface-2)] transition-colors min-h-[44px] ${className}`}
      data-testid="theme-toggle"
      data-theme-current={theme}
      title={isDay ? "Switch to Field Black" : "Switch to Daylight"}
      aria-label="Toggle theme"
    >
      {isDay ? <Moon size={13} /> : <Sun size={13} />}
      <span>{isDay ? "Field Black" : "Daylight"}</span>
    </button>
  );
}

// Bootstrap helper for App.js — apply persisted theme on first render so
// the dark default doesn't flash before useTheme mounts.
export function bootstrapTheme() {
  _applyTheme(_readStored());
}

export default ThemeToggle;
